import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { resolvePatientLocale } from './patientLocale';
import { ArrowLeft, ArrowRight, AlertCircle, CheckCircle2, CreditCard, Tag } from 'lucide-react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Eyebrow,
  FormField,
  Heading,
  Input,
  Skeleton,
  Stepper,
  Textarea,
} from '@insolech/ui';
import {
  patientPublicQueries,
  readViteEnv,
  type PatientPublicView,
  type SubmitReorderInput,
  type SubmitReorderResult,
} from '@insolech/sdk';
import { PatientLayout } from '@/components/layout/PatientLayout';
import { patientProductName } from './productDisplay';
import {
  reorderMarginalPricing,
  reorderLadderSegments,
  reorderCrossUnit,
  reorderCrossTotal,
} from './reorderPricing';
import { CrossSellBox } from './CrossSellBox';
import { FitoBox } from './FitoBox';

/**
 * PatientReorderPage — Phase 4e.
 *
 * 3 steps:
 *   1) Confirm details — show original product summary, optional note for clinic
 *   2) Patient input  — phone, optional email, preferred contact channel
 *   3) Stripe Checkout — POSTs submit-reorder, redirects to checkout_url
 *      (or shows graceful PHASE_4_TODO fallback banner)
 */

const STEPS = ['confirm', 'contact', 'pay'] as const;
type StepKey = typeof STEPS[number];

export function PatientReorderPage() {
  const { t } = useTranslation('patient');
  const navigate = useNavigate();
  const { token, shortCode } = useParams<{ token?: string; shortCode?: string }>();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === '1';

  const [step, setStep] = React.useState<StepKey>('confirm');
  const [quantity, setQuantity] = React.useState(1);
  // Reorder cart R1: pairs of the cross-sell add-on (0 = not added). Kept in
  // the parent so both the Confirm add-on box and the Pay summary read it.
  const [crossQty, setCrossQty] = React.useState(0);
  // Reorder cart R2: FITO add-on (pairs, colour, size). 0 = not added.
  const [fitoQty, setFitoQty] = React.useState(0);
  const [fitoColor, setFitoColor] = React.useState<'black' | 'grey' | 'blue' | null>(null);
  const [fitoSize, setFitoSize] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [preferredContact, setPreferredContact] = React.useState<'phone' | 'whatsapp' | 'email'>('whatsapp');
  const [updateContact, setUpdateContact] = React.useState(true);

  const verify = useQuery({
    queryKey: ['patient-public', token ?? shortCode],
    queryFn: () =>
      patientPublicQueries.verifyQrToken({
        ...(token ? { token } : {}),
        ...(shortCode ? { shortCode } : {}),
      }),
    retry: false,
    enabled: !!(token || shortCode),
  });

  const submit = useMutation<SubmitReorderResult, Error, SubmitReorderInput>({
    mutationFn: (input) => patientPublicQueries.submitReorder(input),
    onSuccess: (data) => {
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      // Stub mode (PHASE_4_TODO) — no checkout URL; jump directly to success page
      // with a query flag so the page can show the "payments not configured" banner.
      const successPath = token
        ? `/p/${encodeURIComponent(token)}/reorder/success?stub=1&reorder_id=${encodeURIComponent(data.reorder_id)}`
        : `/q/${encodeURIComponent(shortCode ?? '')}/reorder/success?stub=1&reorder_id=${encodeURIComponent(data.reorder_id)}`;
      navigate(successPath, { replace: true });
    },
  });

  // R2: seed the FITO size from the original order's foot size once the view
  // loads (a range prefilled its upper bound server-side). Clamp to the
  // offered 35-48 run; fall back to 40 when the source has no usable size.
  const fitoPrefill = verify.data?.fito_prefill_size;
  React.useEffect(() => {
    setFitoSize((prev) => {
      if (prev) return prev;
      const n = fitoPrefill ? parseInt(fitoPrefill, 10) : NaN;
      return Number.isFinite(n) ? String(Math.min(48, Math.max(35, n))) : '40';
    });
  }, [fitoPrefill]);

  if (verify.isLoading) {
    return (
      <PatientLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      </PatientLayout>
    );
  }

  if (verify.isError || !verify.data) {
    return (
      <PatientLayout>
        <Card>
          <CardBody className="text-center py-10">
            <AlertCircle className="h-10 w-10 mx-auto text-error-500" aria-hidden />
            <Heading as="h1" size="h3" className="mt-4">
              {t('not_found_title', 'We couldn\'t find your order')}
            </Heading>
          </CardBody>
        </Card>
      </PatientLayout>
    );
  }

  const view = verify.data;

  // Block reorder if backend says no
  if (!view.actions.can_request_reorder) {
    return (
      <PatientLayout clinicName={view.clinic_name}>
        <Card>
          <CardBody className="text-center py-10">
            <Heading as="h1" size="h3">
              {t('reorder_not_available_title', 'Reorder not yet available')}
            </Heading>
            <p className="mt-3 text-text-secondary">
              {t(
                'reorder_after_delivery',
                'Reorders open once your pair has been delivered. If you think this is a mistake, contact your clinic.',
              )}
            </p>
            <Button asChild variant="outline" className="mt-6">
              <Link to={token ? `/p/${token}` : `/q/${shortCode}`}>
                {t('back_to_status', 'Back to my order')}
              </Link>
            </Button>
          </CardBody>
        </Card>
      </PatientLayout>
    );
  }

  const goNext = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1] as StepKey);
  };
  const goBack = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1] as StepKey);
  };

  // Reorder cart R1: the cross-sell add-on kind maps directly to an item_kind.
  const crossItemKind: 'cross_sandal' | 'cross_insole' | null =
    view.cross_kind === 'sandal' ? 'cross_sandal' : view.cross_kind === 'insole' ? 'cross_insole' : null;

  const handleSubmit = () => {
    const trimmedMessage = message.trim();
    const trimmedPhone = phone.trim();
    const trimmedEmail = email.trim();
    // Cart items: cross-custom add-on and/or FITO. OMIT the key entirely when
    // nothing is added, so a plain reorder body stays byte-identical.
    const items: NonNullable<SubmitReorderInput['items']> = [];
    if (crossItemKind && crossQty > 0) {
      items.push({ item_kind: crossItemKind, quantity: crossQty });
    }
    if (view.fito_offered && fitoQty > 0 && fitoColor && fitoSize) {
      items.push({ item_kind: 'fito_shoe', quantity: fitoQty, fito_color: fitoColor, fito_size: fitoSize });
    }
    submit.mutate({
      ...(token ? { token } : {}),
      ...(shortCode ? { short_code: shortCode } : {}),
      quantity,
      ...(trimmedMessage ? { message: trimmedMessage } : {}),
      preferred_contact: preferredContact,
      // v2.1: phone + email are required (validated on the contact step and
      // re-enforced by submit-reorder), so always send them.
      patient_phone: trimmedPhone,
      patient_email: trimmedEmail,
      update_contact: updateContact,
      // The language the patient actually read this flow in. submit-reorder
      // has always declared + stored preferred_language, but nothing ever sent
      // it, so every reorder recorded 'en'. It now also decides which locale of
      // the public payment-confirmed page Stripe returns them to - without it
      // an Arabic patient completes an RTL reorder and lands on English copy.
      // resolvePatientLocale() is the same resolver PatientLayout used to pick
      // the language this flow is being read in (explicit choice -> browser ->
      // en), and it is typed to the supported locale union.
      preferred_language: resolvePatientLocale(),
      ...(items.length > 0 ? { items } : {}),
    });
  };

  const stepIdx = STEPS.indexOf(step);

  return (
    <PatientLayout clinicName={view.clinic_name}>
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          startIcon={<ArrowLeft className="h-4 w-4 rtl-flip" />}
          onClick={() => navigate(token ? `/p/${token}` : `/q/${shortCode}`)}
          data-testid="patient-reorder-back"
        >
          {t('back_to_status', 'Back to my order')}
        </Button>
      </div>

      <Eyebrow>{t('reorder_eyebrow', 'Reorder')}</Eyebrow>
      <Heading as="h1" size="h1" className="mt-1 mb-6">
        {t('reorder_title', 'Request a new pair of insoles')}
      </Heading>

      {cancelled ? (
        <Alert tone="warning" title={t('reorder_cancelled_title', 'Payment cancelled')} className="mb-4">
          {t('reorder_cancelled_body', 'You can try again whenever you\'re ready.')}
        </Alert>
      ) : null}

      <Stepper
        steps={[
          { id: 'confirm', label: t('reorder_step_confirm', 'Confirm') },
          { id: 'contact', label: t('reorder_step_contact', 'Contact') },
          { id: 'pay', label: t('reorder_step_pay', 'Payment') },
        ]}
        currentStep={stepIdx}
        className="mb-6"
      />

      {step === 'confirm' && (
        <ConfirmStep
          view={view}
          quantity={quantity}
          setQuantity={setQuantity}
          crossQty={crossQty}
          setCrossQty={setCrossQty}
          fitoQty={fitoQty}
          setFitoQty={setFitoQty}
          fitoColor={fitoColor}
          setFitoColor={setFitoColor}
          fitoSize={fitoSize}
          setFitoSize={setFitoSize}
          message={message}
          setMessage={setMessage}
          onNext={goNext}
          t={t}
        />
      )}
      {step === 'contact' && (
        <ContactStep
          phone={phone}
          setPhone={setPhone}
          email={email}
          setEmail={setEmail}
          preferredContact={preferredContact}
          setPreferredContact={setPreferredContact}
          updateContact={updateContact}
          setUpdateContact={setUpdateContact}
          onBack={goBack}
          onNext={goNext}
          t={t}
        />
      )}
      {step === 'pay' && (
        <PayStep
          view={view}
          quantity={quantity}
          crossQty={crossQty}
          fitoQty={fitoQty}
          submitting={submit.isPending}
          error={submit.error?.message ?? null}
          onBack={goBack}
          onSubmit={handleSubmit}
          t={t}
        />
      )}

      <p className="mt-6 text-center text-xs text-text-muted">
        {t('reorder_step_indicator', 'Step {{n}} of {{total}}', {
          n: stepIdx + 1,
          total: STEPS.length,
        })}
      </p>
    </PatientLayout>
  );
}

// -------------------- step views --------------------

const REORDER_QTY_MIN = 1;
const REORDER_QTY_MAX = 10;

/** "1,500" / "1,428.57" style AED amount, browser locale (matches formatDate). */
function formatAed(n: number): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** "10" / "12.5" — a tier percentage without trailing zeros. */
function formatPct(n: number | null | undefined): string {
  if (!Number.isFinite(n)) return '';
  return String(Math.round((n as number) * 100) / 100);
}

function ConfirmStep({
  view,
  quantity,
  setQuantity,
  crossQty,
  setCrossQty,
  fitoQty,
  setFitoQty,
  fitoColor,
  setFitoColor,
  fitoSize,
  setFitoSize,
  message,
  setMessage,
  onNext,
  t,
}: {
  view: PatientPublicView;
  quantity: number;
  setQuantity: (n: number) => void;
  crossQty: number;
  setCrossQty: (n: number) => void;
  fitoQty: number;
  setFitoQty: (n: number) => void;
  fitoColor: 'black' | 'grey' | 'blue' | null;
  setFitoColor: (c: 'black' | 'grey' | 'blue') => void;
  fitoSize: string;
  setFitoSize: (s: string) => void;
  message: string;
  setMessage: (s: string) => void;
  onNext: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const hasPrice = view.reorder_price_aed != null;
  const pricing = reorderMarginalPricing(
    view.reorder_price_aed,
    view.reorder_second_pair_gross_aed,
    view.reorder_additional_pair_gross_aed,
    quantity,
  );
  const segments = reorderLadderSegments(pricing.pairs);
  // Cart R1: applicable cross-sell unit (server prices; we only pick which
  // one applies by anchor qty) and this cart's grand total.
  const crossUnit = reorderCrossUnit(
    view.cross_unit_full_aed,
    view.cross_unit_discounted_aed,
    view.cross_discount_applies_from_anchor_qty,
    quantity,
  );
  const crossTotal = reorderCrossTotal(crossUnit, crossQty);
  const showCross = !!view.cross_kind && hasPrice;
  // R2 FITO: flat per-pair, both anchors.
  const fitoGross = view.fito_gross_aed ?? 0;
  const fitoTotal = view.fito_offered && fitoQty > 0 ? Math.round(fitoGross * fitoQty * 100) / 100 : 0;
  const showFito = !!view.fito_offered && fitoGross > 0 && hasPrice;
  const grandTotal =
    Math.round((pricing.total + crossTotal + fitoTotal + Number.EPSILON) * 100) / 100;
  const anyAddOn = crossQty > 0 || fitoQty > 0;
  // Gate the savings banner on the SAME per-pair gross fields the ladder uses (not
  // the tier percentages), so banner and ladder degrade together: if a deploy skew
  // ever left the pair-gross fields absent, both hide rather than the banner
  // promising a discount the full-price ladder wouldn't show. Only when they
  // actually undercut the preset (a real discount, not 0% tiers).
  const base = view.reorder_price_aed ?? 0;
  const hasTiers =
    view.reorder_second_pair_gross_aed != null &&
    view.reorder_additional_pair_gross_aed != null &&
    (view.reorder_second_pair_gross_aed < base || view.reorder_additional_pair_gross_aed < base);
  return (
    <Card>
      <CardBody className="space-y-4">
        <h2 className="text-base font-semibold">
          {t('reorder_confirm_heading', 'Confirm what you\'d like to reorder')}
        </h2>
        <div className="rounded-md bg-surface-raised border border-border-subtle p-3">
          <p className="text-xs uppercase tracking-wider text-text-muted">
            {t('reorder_original_label', 'Your original insoles')}
          </p>
          <p className="text-sm font-medium mt-1">{patientProductName(view.product_name, t)}</p>
          {view.material ? (
            <p className="text-xs text-text-secondary mt-0.5">
              {t('reorder_material', 'Material: {{material}}', { material: view.material })}
            </p>
          ) : null}
          {view.delivered_at ? (
            <p className="text-xs text-text-secondary mt-1">
              {t('delivered_on', 'Delivered on {{date}}', { date: formatDate(view.delivered_at) })}
            </p>
          ) : null}
        </div>

        {/* Volume-discount banner — brand styled, the main pull to buy more. */}
        {hasPrice && hasTiers ? (
          <div
            className="rounded-xl border border-brand-200 bg-brand-50 p-4 flex items-start gap-3"
            data-testid="reorder-savings-banner"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
              <Tag className="h-5 w-5" />
            </span>
            <div className="text-sm">
              <p className="font-bold text-brand-800">
                {t('reorder_savings_title', 'Buy more, save more')}
              </p>
              <p className="text-brand-700 mt-0.5">
                {t(
                  'reorder_savings_body',
                  'Your 2nd pair is {{t2}}% off, and every pair from the 3rd is {{t3}}% off.',
                  { t2: formatPct(view.reorder_tier2_pct), t3: formatPct(view.reorder_tier3_pct) },
                )}
              </p>
            </div>
          </div>
        ) : null}

        {/* Quantity + live, tiered total. Prices come from the same
            reorder_unit_gross() the server charges, so this is exact, not an
            estimate; the server still re-validates 1..10. */}
        <div>
          <p className="text-sm font-medium mb-2">
            {t('reorder_quantity_label', 'How many pairs?')}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(REORDER_QTY_MIN, quantity - 1))}
              disabled={quantity <= REORDER_QTY_MIN}
              aria-label={t('reorder_qty_decrease', 'Decrease quantity')}
              className="h-9 w-9 rounded-md border border-border-default bg-surface-elevated text-lg leading-none disabled:opacity-40"
              data-testid="reorder-qty-minus"
            >
              −
            </button>
            <span
              className="min-w-[2.5rem] text-center text-base font-semibold tabular-nums"
              data-testid="reorder-qty-value"
              aria-live="polite"
            >
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => setQuantity(Math.min(REORDER_QTY_MAX, quantity + 1))}
              disabled={quantity >= REORDER_QTY_MAX}
              aria-label={t('reorder_qty_increase', 'Increase quantity')}
              className="h-9 w-9 rounded-md border border-border-default bg-surface-elevated text-lg leading-none disabled:opacity-40"
              data-testid="reorder-qty-plus"
            >
              +
            </button>
          </div>
          {hasPrice ? (
            <div className="mt-3 space-y-1" data-testid="reorder-live-total">
              {/* Per-pair ladder, e.g. 1,500 + 1,350 + 1,200 x 2 = 5,250. Pinned
                  dir=ltr so the math reads left-to-right even in RTL (Arabic),
                  like phone numbers. The discount applies to each incremental
                  pair, so this shows exactly what the server charges. */}
              <p className="text-sm">
                <span dir="ltr" className="inline-block tabular-nums" data-testid="reorder-ladder">
                  <span className="text-text-secondary">
                    {segments
                      .map((s) =>
                        s.count > 1 ? `${formatAed(s.price)} × ${s.count}` : formatAed(s.price),
                      )
                      .join(' + ')}
                    {' = '}
                  </span>
                  <span className="text-base font-bold text-text-primary" data-testid="reorder-total">
                    {formatAed(pricing.total)} AED
                  </span>
                </span>
              </p>
              <p className="text-xs text-text-muted">
                {t('reorder_vat_included_short', '(VAT included)')}
              </p>
              {pricing.savings > 0 ? (
                <p
                  className="text-sm font-semibold text-success-700"
                  data-testid="reorder-you-save"
                >
                  {t('reorder_you_save', 'You save {{amount}} AED', {
                    amount: formatAed(pricing.savings),
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Cart R1: cross-sell add-on box (server-priced). Purely additive -
            hidden entirely when the token has no cross_kind, so the plain
            reorder view is unchanged. */}
        {showCross ? (
          <CrossSellBox
            crossKind={view.cross_kind as 'sandal' | 'insole'}
            unitAed={crossUnit}
            fullAed={view.cross_unit_full_aed ?? null}
            discountedAed={view.cross_unit_discounted_aed ?? null}
            discountApplies={crossUnit < (view.cross_unit_full_aed ?? crossUnit)}
            unlockAtAnchorQty={view.cross_discount_applies_from_anchor_qty ?? null}
            anchorQty={quantity}
            crossQty={crossQty}
            setCrossQty={setCrossQty}
            maxQty={REORDER_QTY_MAX}
            t={t}
          />
        ) : null}

        {/* FITO stock-shoe box (box B). Additive; hidden when not offered. */}
        {showFito ? (
          <FitoBox
            grossAed={fitoGross}
            retailAed={view.fito_retail_reference_aed ?? null}
            qty={fitoQty}
            setQty={setFitoQty}
            color={fitoColor}
            setColor={setFitoColor}
            size={fitoSize}
            setSize={setFitoSize}
            maxQty={REORDER_QTY_MAX}
            t={t}
          />
        ) : null}

        {/* Grand total appears only once an add-on is chosen, so the plain
            reorder keeps the single anchor total above unchanged. */}
        {(showCross || showFito) && anyAddOn ? (
          <div
            className="rounded-md border border-border-default bg-surface-raised p-3 flex items-center justify-between"
            data-testid="reorder-grand-total-row"
          >
            <span className="text-sm font-medium">{t('reorder_cart_grand_total', 'Order total')}</span>
            <span dir="ltr" className="text-base font-bold tabular-nums" data-testid="reorder-grand-total">
              {formatAed(grandTotal)} AED
            </span>
          </div>
        ) : null}

        {/* Custom sandals have design choices the patient cannot self-serve
            (unlike FITO, which is fully specified by colour + size). Set the
            expectation that a team member will call to detail the design, and
            reassure that payment is complete. Shown ONLY when a cross_sandal is
            actually in the cart (never for FITO-only or plain reorders). */}
        {showCross && view.cross_kind === 'sandal' && crossQty > 0 ? (
          <Alert
            tone="info"
            title={t('reorder_sandal_call_title', "We'll call you about your sandal design")}
            data-testid="reorder-sandal-call-banner"
          >
            {t(
              'reorder_sandal_call_body',
              'Your payment is complete and covers your sandals in full. An INSOLECH team member will call you by phone to choose the colours and options for your custom sandals. The call is only to detail the design, with nothing more to pay.',
            )}
          </Alert>
        ) : null}

        <p className="text-sm text-text-secondary">
          {t(
            'reorder_confirm_body',
            "You're reordering an exact replica of your current pair. Small tweaks are fine, like the cover colour or going one size up or down; just tell us below. Changes to the design or prescription aren't possible on reorders. For those, please contact your clinic for a new assessment.",
          )}
        </p>
        <FormField
          label={t('form_message_label', 'Message to your clinic (optional)')}
          htmlFor="reorder-message"
        >
          <Textarea
            id="reorder-message"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t(
              'form_message_placeholder',
              'e.g. The arch support has worn down and I want a slightly firmer feel.',
            )}
            data-testid="reorder-message-input"
          />
        </FormField>
        <div className="flex justify-end pt-2">
          <Button
            onClick={onNext}
            endIcon={<ArrowRight className="h-4 w-4 rtl-flip" />}
            data-testid="reorder-next-1"
          >
            {t('action_continue', 'Continue')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ContactStep({
  phone,
  setPhone,
  email,
  setEmail,
  preferredContact,
  setPreferredContact,
  updateContact,
  setUpdateContact,
  onBack,
  onNext,
  t,
}: {
  phone: string;
  setPhone: (s: string) => void;
  email: string;
  setEmail: (s: string) => void;
  preferredContact: 'phone' | 'whatsapp' | 'email';
  setPreferredContact: (s: 'phone' | 'whatsapp' | 'email') => void;
  updateContact: boolean;
  setUpdateContact: (b: boolean) => void;
  onBack: () => void;
  onNext: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // v2.1: BOTH phone and email are required (matches the submit-reorder server
  // gate). Validate format; only continue when both are valid.
  const phoneTrim = phone.trim();
  const emailTrim = email.trim();
  const phoneValid = /^\+?[0-9][0-9\s()-]{6,19}$/.test(phoneTrim);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);
  const canContinue = phoneValid && emailValid;
  return (
    <Card>
      <CardBody className="space-y-4">
        <h2 className="text-base font-semibold">
          {t('reorder_contact_heading', 'How can your clinic reach you?')}
        </h2>
        <FormField
          label={t('form_phone_label', 'Phone number')}
          htmlFor="reorder-phone"
          helper={t('form_phone_hint', 'Include country code, e.g. +971 50 123 4567')}
          {...(phoneTrim.length > 0 && !phoneValid
            ? { error: t('form_phone_invalid', 'Please enter a valid phone number with country code.') }
            : {})}
        >
          <Input
            id="reorder-phone"
            type="tel"
            inputMode="tel"
            dir="ltr"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+971 50 123 4567"
            data-testid="reorder-phone-input"
            aria-invalid={phoneTrim.length > 0 && !phoneValid}
          />
        </FormField>
        <FormField
          label={t('form_email_label', 'Email')}
          htmlFor="reorder-email"
          {...(emailTrim.length > 0 && !emailValid
            ? { error: t('form_email_invalid', 'Please enter a valid email, e.g. you@example.com') }
            : {})}
        >
          <Input
            id="reorder-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="reorder-email-input"
            aria-invalid={emailTrim.length > 0 && !emailValid}
          />
        </FormField>
        <div>
          <p className="text-sm font-medium mb-2">
            {t('form_contact_label', 'Best way to reach you')}
          </p>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {(['whatsapp', 'email', 'phone'] as const).map((opt) => {
              const selected = preferredContact === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setPreferredContact(opt)}
                  className={`px-3 py-2 rounded-md border text-sm transition ${
                    selected
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-border-subtle bg-surface-raised text-text-secondary hover:border-brand-300'
                  }`}
                  data-testid={`reorder-contact-${opt}`}
                >
                  {t(`contact_option_${opt}`, capitalize(opt))}
                </button>
              );
            })}
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={updateContact}
            onChange={(e) => setUpdateContact(e.target.checked)}
            className="mt-0.5"
            data-testid="reorder-update-contact"
          />
          <span className="text-text-secondary">
            {t(
              'form_update_contact',
              'Update my contact details with my clinic so they can reach me on this number.',
            )}
          </span>
        </label>
        <div className="flex justify-between pt-2 gap-3">
          <Button variant="ghost" onClick={onBack} startIcon={<ArrowLeft className="h-4 w-4 rtl-flip" />}>
            {t('action_back', 'Back')}
          </Button>
          <Button
            onClick={onNext}
            disabled={!canContinue}
            endIcon={<ArrowRight className="h-4 w-4 rtl-flip" />}
            data-testid="reorder-next-2"
          >
            {t('action_continue', 'Continue')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function PayStep({
  view,
  quantity,
  crossQty,
  fitoQty,
  submitting,
  error,
  onBack,
  onSubmit,
  t,
}: {
  view: PatientPublicView;
  quantity: number;
  crossQty: number;
  fitoQty: number;
  submitting: boolean;
  error: string | null;
  onBack: () => void;
  onSubmit: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const anchorTotal = reorderMarginalPricing(
    view.reorder_price_aed,
    view.reorder_second_pair_gross_aed,
    view.reorder_additional_pair_gross_aed,
    quantity,
  ).total;
  const crossUnit = reorderCrossUnit(
    view.cross_unit_full_aed,
    view.cross_unit_discounted_aed,
    view.cross_discount_applies_from_anchor_qty,
    quantity,
  );
  const crossTotal = reorderCrossTotal(crossUnit, crossQty);
  const hasCross = !!view.cross_kind && crossQty > 0;
  const fitoGross = view.fito_gross_aed ?? 0;
  const fitoTotal = view.fito_offered && fitoQty > 0 ? Math.round(fitoGross * fitoQty * 100) / 100 : 0;
  const hasFito = !!view.fito_offered && fitoQty > 0;
  const grandTotal =
    Math.round((anchorTotal + crossTotal + fitoTotal + Number.EPSILON) * 100) / 100;
  const hasAnyAddOn = hasCross || hasFito;
  const crossLabel =
    view.cross_kind === 'sandal'
      ? t('reorder_cross_sandals_label', 'Custom orthopaedic sandals')
      : t('reorder_cross_insoles_label', 'Custom insoles');
  // Detect stub mode at runtime via the publishable key. readViteEnv reads it
  // as a literal import.meta.env.VITE_* expression that Vite statically inlines
  // (an aliased `const env = import.meta.env; env.VITE_*` is NOT inlined and
  // read as undefined in production — the bug this replaces). Safe to read: a
  // public publishable key, not the secret.
  const stripePub = readViteEnv('VITE_STRIPE_PUBLISHABLE_KEY');
  const stripeConfigured = !!stripePub && stripePub.startsWith('pk_') && stripePub !== 'pk_test_replace_me';

  return (
    <Card>
      <CardBody className="space-y-4">
        <h2 className="text-base font-semibold">
          {t('reorder_pay_heading', 'Almost done, confirm and pay')}
        </h2>
        <div className="rounded-md bg-surface-raised border border-border-subtle p-3 space-y-1">
          <div className="flex justify-between">
            <span className="text-sm text-text-secondary">
              {t('reorder_summary_product', 'Reorder')}
            </span>
            <span className="text-sm font-medium">{patientProductName(view.product_name, t)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-text-secondary">
              {t('reorder_summary_quantity', 'Quantity')}
            </span>
            <span className="text-sm tabular-nums">
              {t('reorder_summary_pairs', {
                count: quantity,
                defaultValue: '{{count}} pair',
                defaultValue_other: '{{count}} pairs',
              })}
            </span>
          </div>
          {view.reorder_price_aed != null ? (
            <div className="flex justify-between">
              <span className="text-sm text-text-secondary">
                {t('reorder_summary_total', 'Total')}
              </span>
              <span className="text-sm font-semibold tabular-nums" data-testid="reorder-pay-total">
                {formatAed(anchorTotal)}{' '}
                AED
                <span className="font-normal text-text-muted">
                  {' '}
                  {t('reorder_vat_included_short', '(VAT included)')}
                </span>
              </span>
            </div>
          ) : null}
          {/* Cart: add-on lines + grand total, only when something is added.
              No-add reorders keep the single total row above. */}
          {hasCross ? (
            <div className="flex justify-between" data-testid="reorder-pay-cross-row">
              <span className="text-sm text-text-secondary">
                {crossLabel}{' '}
                <span className="text-text-muted">
                  {t('reorder_summary_pairs', {
                    count: crossQty,
                    defaultValue: '{{count}} pair',
                    defaultValue_other: '{{count}} pairs',
                  })}
                </span>
              </span>
              <span className="text-sm tabular-nums">{formatAed(crossTotal)} AED</span>
            </div>
          ) : null}
          {hasFito ? (
            <div className="flex justify-between" data-testid="reorder-pay-fito-row">
              <span className="text-sm text-text-secondary">
                {t('reorder_fito_label', 'Fito Model 1 therapeutic shoes')}{' '}
                <span className="text-text-muted">
                  {t('reorder_summary_pairs', {
                    count: fitoQty,
                    defaultValue: '{{count}} pair',
                    defaultValue_other: '{{count}} pairs',
                  })}
                </span>
              </span>
              <span className="text-sm tabular-nums">{formatAed(fitoTotal)} AED</span>
            </div>
          ) : null}
          {hasAnyAddOn ? (
            <div className="flex justify-between border-t border-border-subtle pt-1 mt-1">
              <span className="text-sm font-semibold">
                {t('reorder_cart_grand_total', 'Order total')}
              </span>
              <span className="text-sm font-bold tabular-nums" data-testid="reorder-pay-grand-total">
                {formatAed(grandTotal)}{' '}
                AED
                <span className="font-normal text-text-muted">
                  {' '}
                  {t('reorder_vat_included_short', '(VAT included)')}
                </span>
              </span>
            </div>
          ) : null}
          <div className="flex justify-between">
            <span className="text-sm text-text-secondary">
              {t('reorder_summary_clinic', 'Clinic')}
            </span>
            <span className="text-sm">{view.clinic_name}</span>
          </div>
        </div>

        {!stripeConfigured ? (
          <Alert
            tone="info"
            title={t('reorder_payments_not_configured_title', 'Payments are not yet configured')}
          >
            {t(
              'reorder_payments_not_configured_body',
              'Your reorder request will be recorded. Please contact your clinic to arrange your reorder and payment. No card information will be collected here.',
            )}
          </Alert>
        ) : (
          <p className="text-sm text-text-secondary">
            {t(
              'reorder_pay_body',
              'You\'ll be redirected to Stripe to complete payment securely. Your clinic will start your new pair as soon as the payment clears.',
            )}
          </p>
        )}

        {/* Reassurance for a custom-sandal add-on: payment is complete and a
            team member will call to detail the design. Cross_sandal only. */}
        {hasCross && view.cross_kind === 'sandal' ? (
          <Alert
            tone="info"
            title={t('reorder_sandal_call_title', "We'll call you about your sandal design")}
            data-testid="reorder-pay-sandal-call-banner"
          >
            {t(
              'reorder_sandal_call_body',
              'Your payment is complete and covers your sandals in full. An INSOLECH team member will call you by phone to choose the colours and options for your custom sandals. The call is only to detail the design, with nothing more to pay.',
            )}
          </Alert>
        ) : null}

        {error ? (
          <Alert tone="error" title={t('reorder_error_title', 'Something went wrong')}>
            {error}
          </Alert>
        ) : null}

        <div className="flex justify-between pt-2 gap-3">
          <Button
            variant="ghost"
            onClick={onBack}
            startIcon={<ArrowLeft className="h-4 w-4 rtl-flip" />}
            disabled={submitting}
          >
            {t('action_back', 'Back')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={submitting}
            isLoading={submitting}
            startIcon={<CreditCard className="h-4 w-4" />}
            data-testid="reorder-submit"
          >
            {stripeConfigured
              ? t('reorder_action_pay', 'Continue to payment')
              : t('reorder_action_send_request', 'Send my request')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// -------------------- helpers --------------------

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Re-export the success page as a separate route component too
export function PatientReorderSuccessPage() {
  const { t } = useTranslation('patient');
  const { token, shortCode } = useParams<{ token?: string; shortCode?: string }>();
  const [searchParams] = useSearchParams();
  const isStub = searchParams.get('stub') === '1';

  return (
    <PatientLayout>
      <Card>
        <CardBody className="text-center py-10 space-y-4">
          <CheckCircle2 className="h-12 w-12 mx-auto text-success-500" aria-hidden />
          <Heading as="h1" size="h3">
            {isStub
              ? t('thank_you_title_stub', 'Request received')
              : t('thank_you_title', 'Thank you')}
          </Heading>
          <p className="text-text-secondary max-w-md mx-auto">
            {isStub
              ? t(
                  'thank_you_body_stub',
                  'Your reorder request has been recorded. Please contact your clinic to arrange your reorder and payment.',
                )
              : t(
                  'thank_you_body_paid',
                  'Your payment has been received. Your clinic will start crafting your new pair shortly.',
                )}
          </p>
          <Button asChild variant="outline" className="mt-2" data-testid="reorder-back-status">
            <Link to={token ? `/p/${token}` : `/q/${shortCode}`}>
              {t('back_to_status', 'Back to my order')}
            </Link>
          </Button>
        </CardBody>
      </Card>
    </PatientLayout>
  );
}
